import { type ReactNode } from "react";
import { useIntl } from "react-intl";

import { Divider } from "@/components/Divider/Divider";
import { Image } from "@/components/Image/Image";
import { Select, SelectItem } from "@/components/Select";
import { type PaymentType, usePaymentContext } from "@/contexts/PaymentContext";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

type PaymentTypeRadioProps = {
  value: PaymentType;
  checked: boolean;
  onChange: (type: PaymentType) => void;
  disabled?: boolean;
  label: ReactNode;
  description: ReactNode;
  badge?: ReactNode;
};

const PaymentTypeRadio = ({
  value,
  checked,
  onChange,
  disabled,
  label,
  description,
  badge,
}: PaymentTypeRadioProps) => {
  return (
    <label className="relative flex flex-1 cursor-pointer flex-col items-start gap-y-2 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:enabled:border-gray-300 has-checked:border-2 has-checked:border-core-accent-500 has-checked:bg-green-50 has-disabled:cursor-not-allowed has-disabled:opacity-60">
      <input
        type="radio"
        name="paymentType"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="peer hidden"
        disabled={disabled}
      />
      {badge ? (
        <span className="absolute end-4 top-0 -translate-y-1/2 rounded-full bg-surface-accent-softer px-2 py-1 text-xs font-medium text-text-primary">
          {badge}
        </span>
      ) : null}
      <div className="text-sm font-medium text-text-primary">{label}</div>
      <div className="text-xs text-text-tertiary">{description}</div>
    </label>
  );
};

type PaymentChooserProps = {
  price: number;
};

export const PaymentChooser = ({ price }: PaymentChooserProps) => {
  const intl = useIntl();
  const {
    paymentType,
    handlePaymentTypeChange,
    selectedInstallments,
    availableInstallments,
    sessionId,
    setInstallments,
  } = usePaymentContext();

  const isBnplDisabled = !sessionId;

  return (
    <>
      <Image src="/bnpl.png" className="rounded-xl" />

      <div className="my-4 w-full">
        <div className="flex w-full gap-3">
          <PaymentTypeRadio
            value="standard"
            checked={paymentType === "standard"}
            onChange={handlePaymentTypeChange}
            label={intl.formatMessage({
              defaultMessage: "Standard",
              description: "Short label for the standard upfront payment type",
            })}
            description={intl.formatMessage({
              defaultMessage: "Pay the full amount upfront",
              description:
                "Description of the standard pay-upfront payment option",
            })}
          />

          <PaymentTypeRadio
            value="bnpl"
            checked={paymentType === "bnpl"}
            onChange={handlePaymentTypeChange}
            label={intl.formatMessage({
              defaultMessage: "Pay later",
              description:
                "Short label for the pay-later payment type in the payment list",
            })}
            description={intl.formatMessage({
              defaultMessage:
                "Get your item now and split the cost into monthly instalments",
              description:
                "Description of the split / instalment payment option explaining how it works",
            })}
            badge={
              isBnplDisabled
                ? null
                : intl.formatMessage({
                    defaultMessage: "Flexible!",
                    description:
                      "Badge label on the flexible / new instalment payment option",
                  })
            }
            disabled={isBnplDisabled}
          />
        </div>

        <div className="mt-4 rounded-xl bg-surface-neutral-soft p-4">
          {paymentType === "standard" && (
            <>
              <p className="mb-1 text-sm font-medium text-text-title">
                {intl.formatMessage({
                  defaultMessage: "Price",
                  description:
                    "Label for the ticket or booking base price in the payment summary",
                })}
              </p>
              <div className="text-xl font-bold text-text-primary">
                {formatCurrency(price)}
              </div>
            </>
          )}
          {paymentType === "bnpl" && (
            <>
              <p className="text-start text-sm font-medium text-text-primary">
                {intl.formatMessage({
                  defaultMessage: "Create your plan",
                  description:
                    "Section heading for the interactive instalment payment calculator",
                })}
              </p>
              <p className="mb-5 text-start text-xs text-text-tertiary">
                {intl.formatMessage({
                  defaultMessage: "Select how long you would like to pay",
                  description:
                    "Instructional text below the payment calculator heading",
                })}
              </p>
              <Select
                label={intl.formatMessage({
                  defaultMessage: "Number of instalments",
                  description:
                    "Label for the number of instalments in the payment plan",
                })}
                value={selectedInstallments}
                onChange={(e) => setInstallments(Number(e.target.value))}
              >
                {availableInstallments?.map((num) => (
                  <SelectItem key={num} value={num}>
                    {intl.formatMessage(
                      {
                        defaultMessage: "{count} months",
                        description: "Month count label in the instalment plan",
                      },
                      { count: num },
                    )}
                  </SelectItem>
                ))}
              </Select>

              <div className="my-4">
                <Divider />
              </div>

              <div className="mb-1 text-sm font-medium text-text-title">
                {intl.formatMessage({
                  defaultMessage: "Monthly payment",
                  description:
                    "Label for the monthly instalment amount in the payment chooser",
                })}
              </div>
              <div className="text-xl font-bold text-text-primary">
                <span>
                  {formatCurrency(
                    calculateMonthlyInstallment(
                      includeBnplFee(price),
                      selectedInstallments,
                    ),
                  )}
                </span>{" "}
                <span className="text-xs font-normal text-text-tertiary">
                  {intl.formatMessage({
                    defaultMessage: "per month",
                    description:
                      "Short 'per month' unit label used next to instalment prices",
                  })}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
